import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../utils/api';
import { profiloDaCompletare } from '../utils/profilo';
import { useAuth } from '../context/AuthContext';
import { consulenteDaMostrare } from '../components/cliente/useConsulente';
import { statoHome, novita, adessoRoma } from '../utils/percorso';
import {
  Intestazione, CardRicerca, CardPercorso, CardImmobili, CardAggiungi, CardNovita, CardDaFare, CardDocumenti, RigaDisponibilita, RigaProfilo, RigaProposta,
} from '../components/cliente/SezioniHome';
import { scadenzaProposta } from '../utils/scheda';
import CardOggi from '../components/cliente/CardOggi';
import CardNotifiche from '../components/cliente/CardNotifiche';
import CardInstalla from '../components/cliente/CardInstalla';
import Icona from '../components/cliente/Icone';
import { disponibilitaDaChiedere } from '../utils/agenda';
import CardEsito from '../components/cliente/CardEsito';
import { useTraduzioni, PRINCIPALE } from '../i18n';
import { ROTTA_PERMESSI, dataItaliana, permessoDaRinnovare } from './RinnovoPermesso';

// Home del cliente (/dashboard): bozza approvata design/home-cliente-v8.html.
// Si compone con le richieste che il backend ha già; la fase, la card "Oggi", gli esiti in sospeso e il
// prossimo appuntamento li calcola utils/percorso.js (stessa regola del server). L'ora "adesso" avanza da
// sola: la visita passa da "Oggi" a "Com'è andata?" all'orario giusto senza ricaricare la pagina. È l'ora di Roma
// (adessoRoma), come le date delle visite, anche con il browser su un altro fuso.

const OGNI_MINUTO = 30 * 1000;

// Il dato di una chiamata secondaria; se è fallita null (la card dice "non disponibile", mai dati inventati)
const valore = (risultato) => (risultato.status === 'fulfilled' ? risultato.value.data ?? null : null);

// Riga discreta: qualcuno del nucleo ha il permesso di soggiorno in scadenza (o già scaduto). Porta alla pagina
// «Rinnovo del permesso», che spiega passo per passo cosa fare. I dati arrivano dalla rotta dei permessi: finché
// non c'è (o se risponde male) qui non compare niente — mai scadenze inventate.
export const RigaPermesso = ({ permesso }) => {
  const { t, l, data: dataInLingua } = useTraduzioni();
  if (!permesso?.scadenza) return null;
  const scaduto = typeof permesso.giorni_mancanti === 'number' && permesso.giorni_mancanti < 0;
  const quando = l === PRINCIPALE ? dataItaliana(permesso.scadenza) : dataInLingua(permesso.scadenza);
  return (
    <div className="search r" data-testid="riga-permesso">
      <div className="items">
        <div className="it">
          <div className="i"><Icona nome="avviso" /></div>
          <div>
            <b style={{ fontSize: '1.1rem' }}>
              {t(scaduto ? 'home.permesso.scaduto' : 'home.permesso.scade', { nome: permesso.nome, data: quando })}
            </b>
            <span>{t(permesso.ricevuta ? 'home.permesso.ricevuta' : 'home.permesso.testo')}</span>
          </div>
        </div>
      </div>
      <Link to="/rinnovo-permesso">{t('home.permesso.link')}</Link>
    </div>
  );
};

const HomeCliente = () => {
  const { user, userType } = useAuth();
  const { t, l } = useTraduzioni();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [adesso, setAdesso] = useState(adessoRoma);

  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('comune.menu.percorso_lungo')}`;
    return () => { document.title = precedente; };
  }, [t]);

  useEffect(() => {
    const t = setInterval(() => setAdesso(adessoRoma()), OGNI_MINUTO);
    return () => clearInterval(t);
  }, []);

  const carica = useCallback(async () => {
    if (!user?.id) return;
    const base = `${API}/clienti/${user.id}`;
    const risposte = await Promise.allSettled([
      axios.get(base), axios.get(`${base}/immobili`), axios.get(`${base}/visite`), axios.get(`${base}/proposte`),
      axios.get(`${base}/cronologia`), axios.get(`${base}/documenti-mancanti`), axios.get(`${base}/consulente`),
      axios.get(`${base}/documenti/pratica`), axios.get(ROTTA_PERMESSI(user.id)),
    ]);
    const [cliente, immobili, visite, proposte, cronologia, documenti, consulente, pratica, permessi] = risposte;
    // Le proposte decidono la fase (Proposta, Mutuo, Rogito): senza, la pagina direbbe una fase sbagliata
    if ([cliente, immobili, visite, proposte].some((r) => r.status === 'rejected')) {
      setErrore(true);
      return;
    }
    setErrore(false);
    setAdesso(adessoRoma());
    setDati({
      cliente: cliente.value.data,
      immobili: immobili.value.data || [],
      visite: visite.value.data || [],
      proposte: proposte.value.data || [],
      cronologia: valore(cronologia),
      documentiMancanti: valore(documenti),
      pratica: valore(pratica),
      // la rotta dei permessi può non esserci ancora: se risponde male non si scrive niente (permessoDaRinnovare dà null)
      permesso: permessoDaRinnovare(valore(permessi)),
      consulente: consulenteDaMostrare(valore(consulente), cliente.value.data),
    });
  }, [user?.id]);

  useEffect(() => {
    if (userType !== 'cliente') return;
    carica();
  }, [userType, carica]);

  // "Sposta" rifiutato dal server (visita annullata o già passata nel frattempo): si dice perché e si rilegge la
  // pagina, così la riga non resta "Visita giovedì 17 · 16:00" con Indicazioni fino a una ricarica
  const rifiutata = useCallback((messaggio) => {
    toast(`${messaggio} ${t('home.pagina_aggiornata')}`);
    carica();
  }, [carica, t]);

  // «Sono giuste»: conferma quella che si vede, senza rimandarla (prima la home riscriveva la griglia letta all'apertura
  // e cancellava quella cambiata nel frattempo in un'altra scheda). Se nel frattempo è cambiata (409) si rilegge e lo si dice.
  const confermaDisponibilita = useCallback(async (cliente) => {
    try {
      await axios.put(`${API}/clienti/${cliente.id}/disponibilita`, { conferma: true, versione: cliente.disponibilita?.aggiornata_alle ?? null });
    } catch (err) {
      if (err?.response?.status !== 409) throw err;
      toast(t('home.disponibilita.cambiata'));
    }
    await carica();
  }, [carica, t]);

  const stato = useMemo(() => (dati ? statoHome({ ...dati, cronologia: dati.cronologia || [] }, adesso, l) : null), [dati, adesso, l]);
  const tutteLeNovita = useMemo(() => (dati?.cronologia ? novita(dati.cronologia, dati.immobili, adesso, 12, l) : []), [dati, adesso, l]);

  // Admin e collaboratori hanno la loro pagina
  if (userType === 'admin') return <Navigate to="/admin" replace />;
  if (userType === 'collaboratore') return <Navigate to="/collaboratore" replace />;



  if (!stato) {
    return (
      <main className="mk-home wrap">
        <div className="attesa" role="status">
          {errore ? (
            <div className="err">
              {t('home.errore_caricamento')}{' '}
              <a href="#riprova" onClick={(e) => { e.preventDefault(); carica(); }}><u>{t('comune.riprova')}</u></a>
            </div>
          ) : <span>{t('comune.caricamento')}</span>}
        </div>
      </main>
    );
  }

  const { cliente, immobili, consulente } = dati;
  return (
    <main className="mk-home wrap" data-testid="home-cliente">
      <Intestazione cliente={cliente} adesso={adesso} fase={stato.fase} riepilogo={stato.riepilogo} consulente={consulente} />
      <CardRicerca ricerca={stato.ricerca} />
      <RigaProposta scadenza={scadenzaProposta(dati.proposte, immobili, adesso)} consulente={consulente} adesso={adesso} />
      {/* fasi Mutuo e Rogito (offerta accettata): niente più visite, la domanda non compare (resta nel profilo) */}
      <RigaDisponibilita motivo={disponibilitaDaChiedere(cliente.disponibilita, adesso, stato.fase.chiave)} disponibilita={cliente.disponibilita}
        adesso={adesso} onConferma={() => confermaDisponibilita(cliente)} />
      <RigaPermesso permesso={dati.permesso} />
      <RigaProfilo mostra={profiloDaCompletare(cliente)} />
      {/* L'app sulla schermata Home viene prima delle notifiche: su iPhone senza l'app le notifiche non
          arrivano proprio (components/cliente/CardInstalla.jsx). Su un computer non compare. */}
      <CardInstalla />
      {/* Il permesso delle notifiche si chiede qui, dopo il login e con un tocco: mai al primo caricamento */}
      <CardNotifiche />

      {stato.oggi.length > 0 && <CardOggi appuntamenti={stato.oggi} consulente={consulente} adesso={adesso} onAggiorna={carica} onRifiuto={rifiutata} />}
      {stato.esiti.map((v) => (
        <CardEsito key={v.id} visita={v} immobile={immobili.find((i) => i.id === v.immobile_id)} consulente={consulente}
          adesso={adesso} onFatto={carica} />
      ))}

      <div className="grid g2">
        <div className="grid">
          <CardPercorso fase={stato.fase} prossimo={stato.prossimo} oggi={stato.oggi} adesso={adesso} proposte={dati.proposte} consulente={consulente} />
          <CardImmobili recenti={stato.recenti} totale={immobili.length} inGioco={stato.inGioco} adesso={adesso} consulente={consulente}
            onFatto={carica} onRifiuto={rifiutata} visite={dati.visite} />
          <CardAggiungi clienteId={user.id} onAggiunto={carica} />
        </div>
        <div className="grid">
          <CardNovita voci={stato.novita} tutte={tutteLeNovita} nonDisponibile={!dati.cronologia} onRiprova={carica} />
          <CardDaFare daFare={stato.daFare} nonDisponibile={!dati.documentiMancanti} onRiprova={carica} />
          <CardDocumenti documenti={stato.documenti} nonDisponibile={!dati.pratica} onRiprova={carica} />
        </div>
      </div>
    </main>
  );
};

export default HomeCliente;
